import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Search, X } from "lucide-react";
import { T } from "./pdUtil";
import { Anchored } from "./pdReview";

export const UploadIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 7V5a2 2 0 0 1 2-2h2" /><path d="M17 3h2a2 2 0 0 1 2 2v2" /><path d="M21 17v2a2 2 0 0 1-2 2h-2" /><path d="M7 21H5a2 2 0 0 1-2-2v-2" /><path d="M7 12h10" /></svg>;
const FilterIcon = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke={T.ink} strokeWidth="1.6" aria-hidden="true"><path d="M2.5 4.5h11M4.5 8h7M6.5 11.5h3" /></svg>;

export function usePop() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc); document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);
  return { open, setOpen, ref };
}

// 36px icon button that expands into a search field.
export function SearchExpand({ value, onChange, placeholder }) {
  const [open, setOpen] = useState(!!value);
  if (!open && !value) return <button type="button" className="pd-btn ico" aria-label="Search" onClick={() => setOpen(true)} data-testid="pd-search-toggle"><Search size={14} style={{ color: T.ink }} /></button>;
  return (
    <label className="pd-srch" data-testid="pd-search-box">
      <Search size={14} style={{ color: T.sub }} />
      <input autoFocus value={value} onChange={(e) => onChange(e.target.value)} onBlur={() => !value && setOpen(false)} onKeyDown={(e) => { if (e.key === "Escape") { onChange(""); setOpen(false); } }} placeholder={placeholder} aria-label="Search documents" data-testid="pd-search" />
      {value ? <button type="button" aria-label="Clear search" onClick={() => { onChange(""); setOpen(false); }} data-testid="pd-search-clear"><X size={13} style={{ color: T.mute }} /></button> : null}
    </label>
  );
}

const Box = ({ on }) => <span className={`pd-box${on ? " on" : ""}`}>{on ? <Check size={11} strokeWidth={3} color="#fff" /> : null}</span>;
export const Radio = ({ on, label, onClick, testid }) => (
  <button type="button" className="pd-chk" role="radio" aria-checked={on} onClick={onClick} data-testid={testid}><span className={`pd-radio${on ? " on" : ""}`} />{label}</button>
);
export const CheckRow = ({ on, label, n, onClick, testid, icon }) => (
  <button type="button" className="pd-chk" role="checkbox" aria-checked={on} onClick={onClick} data-testid={testid}><Box on={on} />{icon ? <span className="pd-chk-ic">{icon}</span> : null}<span className="flex-1 text-left truncate min-w-0" title={label}>{label}</span><span className="n" style={{ flex: "none" }}>{n || 0}</span></button>
);
export const Grp = ({ title, children }) => <div><div className="pd-plab">{title}</div>{children}</div>;

// Compact segmented pill control (single choice) for the settings band.
export const Segmented = ({ value, onChange, options, testid }) => (
  <div className="pd-seg" role="group" data-testid={testid}>
    {options.map(([v, t]) => (
      <button key={v} type="button" className={`pd-seg-pill${value === v ? " on" : ""}`} aria-pressed={value === v} onClick={() => onChange(v)} data-testid={`${testid}-${v}`}>{t}</button>
    ))}
  </div>
);

// Document-type picker: search + Selected + Most used (top 6) + "{n} more". The list is
// the only internal scroll region. Grouping by doc-kind isn't available in the data, so
// typing filters a flat list.
export function TypePicker({ options, counts, selected, onToggle, pretty = (v) => v, testid }) {
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();
  const all = options.slice().sort((a, b) => (counts?.[b] || 0) - (counts?.[a] || 0) || pretty(a).localeCompare(pretty(b)));
  const Row = (v) => <CheckRow key={v} on={selected.has(v)} label={pretty(v)} n={counts?.[v]} onClick={() => onToggle(v)} testid={`${testid}-opt-${v}`} />;
  const sel = all.filter((v) => selected.has(v));
  const most = all.filter((v) => !selected.has(v)).slice(0, 6);
  const moreN = all.length - sel.length - most.length;
  const matches = query ? all.filter((v) => pretty(v).toLowerCase().includes(query)) : null;
  return (
    <div className="pd-typewrap">
      <label className="pd-srch pd-type-srch">
        <Search size={13} style={{ color: T.sub }} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a type" aria-label="Find a document type" style={{ width: "100%" }} data-testid={`${testid}-search`} />
        {q ? <button type="button" onClick={() => setQ("")} aria-label="Clear search"><X size={12} style={{ color: T.mute }} /></button> : null}
      </label>
      <div className="pd-stype" data-testid={`${testid}-list`}>
        {query ? (
          matches.length ? matches.map(Row) : <div className="px-2 py-3 text-[12.5px]" style={{ color: T.faint }} data-testid={`${testid}-empty`}>No matches</div>
        ) : (
          <>
            {sel.length ? <><div className="pd-ssub">Selected</div>{sel.map(Row)}</> : null}
            <div className="pd-ssub">Most used</div>
            {most.map(Row)}
            {moreN > 0 ? <div className="pd-smore" data-testid={`${testid}-more`}>{moreN} more · type to find them</div> : null}
          </>
        )}
      </div>
    </div>
  );
}

// Sortable + optionally filterable column header. Clicking the label cycles the
// active sort onto that column; the funnel opens a per-column filter popover.
export function ColHead({ label, align = "left", sortKey, activeSort, onSort, filterActive, filterWidth = 260, resizable = false, onResize, resizeMin = 120, children, testid }) {
  const btnRef = useRef(null);
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const active = sortKey && activeSort === sortKey;
  const onResizeDown = (e) => {
    e.preventDefault(); e.stopPropagation();
    const startW = rootRef.current?.getBoundingClientRect().width || resizeMin;
    const startX = e.clientX;
    const move = (ev) => onResize(Math.max(resizeMin, Math.round(startW + (ev.clientX - startX))));
    const up = () => {
      document.removeEventListener("mousemove", move); document.removeEventListener("mouseup", up);
      document.body.style.cursor = ""; document.body.style.userSelect = "";
    };
    document.addEventListener("mousemove", move); document.addEventListener("mouseup", up);
    document.body.style.cursor = "col-resize"; document.body.style.userSelect = "none";
  };
  return (
    <span ref={rootRef} className={`pd-colh${align === "right" ? " r" : ""}${resizable ? " rz" : ""}`} data-testid={testid}>
      {sortKey ? (
        <button type="button" className={`pd-colh-s${active ? " on" : ""}`} onClick={() => onSort(sortKey)} aria-pressed={active} data-testid={`${testid}-sort`}>
          <span>{label}</span>
          <svg className="pd-colh-caret" width="9" height="9" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><path d="M4 6l4 4 4-4" /></svg>
        </button>
      ) : <span>{label}</span>}
      {children ? (
        <>
          <button type="button" ref={btnRef} className={`pd-colh-f${filterActive ? " on" : ""}`} onClick={() => setOpen((o) => !o)} aria-label={`Filter ${label}`} aria-expanded={open} data-testid={`${testid}-filter`}>
            <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M2.5 4h11M4.5 8h7M6.5 12h3" /></svg>
          </button>
          {open ? <Anchored anchorRef={btnRef} width={filterWidth} onClose={() => setOpen(false)}>{children}</Anchored> : null}
        </>
      ) : null}
      {resizable ? <span className="pd-colrz" role="separator" aria-orientation="vertical" onMouseDown={onResizeDown} onDoubleClick={() => onResize(0)} title="Drag to resize · double-click to reset" data-testid={`${testid}-resize`} /> : null}
    </span>
  );
}

// Searchable checkbox list — for unbounded option sets (e.g. document type).
export function TypeaheadChecks({ options, counts, selected, onToggle, pretty = (v) => v, placeholder = "Search…", testid }) {
  const [q, setQ] = useState("");
  const list = options.filter((v) => pretty(v).toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div style={{ padding: 8, display: "flex", flexDirection: "column", gap: 8, maxHeight: "min(60vh, 420px)" }}>
      <label className="pd-srch" style={{ minHeight: 32 }}>
        <Search size={13} style={{ color: T.sub }} />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} aria-label={placeholder} style={{ width: "100%" }} data-testid={`${testid}-search`} />
        {q ? <button type="button" onClick={() => setQ("")} aria-label="Clear search"><X size={12} style={{ color: T.mute }} /></button> : null}
      </label>
      <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", minHeight: 0 }} data-testid={`${testid}-list`}>
        {list.length ? list.map((v) => <CheckRow key={v} on={selected.has(v)} label={pretty(v)} n={counts?.[v]} onClick={() => onToggle(v)} testid={`${testid}-opt-${v}`} />)
          : <div className="px-2 py-3 text-[12.5px]" style={{ color: T.faint }} data-testid={`${testid}-empty`}>No matches</div>}
      </div>
    </div>
  );
}

// "Sort & Filter · N" button + draft/apply panel (§3). Picks change a DRAFT only;
// the list keeps showing the applied state until "Show {n} documents" commits it.
// snapshot() → the current applied state as a plain draft object; apply(draft) commits;
// clearAllDraft(draft) → draft with every filter cleared; countDraft(draft) → row count.
// children is a render-prop: (draft, patch) => JSX.
export function SortFilter({ count, width, snapshot, apply, clearAllDraft, countDraft, children, wide, cardRef, footerLeft }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const sheetRef = useRef(null);
  const openPanel = () => { setDraft(snapshot()); setOpen(true); };
  const patch = (p) => setDraft((d) => ({ ...d, ...p }));
  const n = draft ? countDraft(draft) : count;
  const dirty = draft ? JSON.stringify(draft) !== JSON.stringify(snapshot()) : false;

  const place = useCallback(() => {
    const card = cardRef?.current; const trig = triggerRef.current;
    if (!card || !trig) return;
    // The sheet is position:fixed inside body{zoom:z}, so any top/left/width we assign is
    // multiplied by z on render. getBoundingClientRect() already returns zoomed (visual) px,
    // so we divide everything back to layout px and assign in that space.
    const z = parseFloat(getComputedStyle(document.body).zoom) || 1;
    const cr = card.getBoundingClientRect();
    const tr = trig.getBoundingClientRect();
    const nav = document.querySelector("nav.rq-sidenav");
    const navRight = (nav ? nav.getBoundingClientRect().right / z : 0) + 8;
    const vw = window.innerWidth / z;
    const cardWidth = cr.width / z;
    const inset = 12, maxW = 1180;
    let w = Math.min(cardWidth - inset * 2, maxW);
    w = Math.min(w, vw - navRight - 12);
    // Right-align the sheet under the Sort & Filter button (its right edge tracks the trigger),
    // then clamp so it never crosses the sidenav or the viewport edge.
    let left = tr.right / z - w;
    left = Math.max(navRight, Math.min(left, vw - w - 8));
    // Prefer opening below the trigger; if there isn't enough room there, flip above it so the
    // footer is never clipped on short/zoomed viewports.
    const vh = window.innerHeight / z;
    const below = vh - tr.bottom / z - 12;
    const above = tr.top / z - 12;
    let top, maxH;
    if (below >= 300 || below >= above) { top = tr.bottom / z + 6; maxH = Math.min(620, below); }
    else { maxH = Math.min(620, above); top = tr.top / z - 6 - maxH; }
    setPos({ top, left, w, maxH, cols: w >= 900 ? 4 : w >= 620 ? 2 : 1 });
  }, [cardRef]);
  useLayoutEffect(() => { if (open && wide) place(); }, [open, wide, place]);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (triggerRef.current?.contains(e.target) || sheetRef.current?.contains(e.target)) return; setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onMove = () => wide && place();
    document.addEventListener("mousedown", onDoc); document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onMove); window.addEventListener("scroll", onMove, true);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); window.removeEventListener("resize", onMove); window.removeEventListener("scroll", onMove, true); };
  }, [open, wide, place]);

  const trigger = (
    <button type="button" className={`pd-btn${count ? " fon" : ""}`} title="Sort & Filter" aria-label={`Sort & Filter${count ? ` · ${count} active` : ""}`} aria-expanded={open} onClick={() => (open ? setOpen(false) : openPanel())} data-testid="pd-sortfilter-btn"><FilterIcon />{count ? <span className="pd-sf-badge" data-testid="pd-sortfilter-count">{count}</span> : null}</button>
  );
  const footer = (
    <div className="pd-pfoot">
      <button type="button" className="pd-btn txt" onClick={() => setDraft(clearAllDraft(draft))} data-testid="pd-sf-clear">Clear all</button>
      {footerLeft || null}
      <span className="flex-1" />
      <span className="pd-sf-status" data-testid="pd-sf-status">{dirty ? "Not applied yet" : "Also on each column header"}</span>
      <button type="button" className="pd-btn pri" onClick={() => { apply(draft); setOpen(false); }} data-testid="pd-sf-apply">Show {n} {n === 1 ? "document" : "documents"}</button>
    </div>
  );

  if (wide) {
    return (
      <div className="relative" ref={triggerRef}>
        {trigger}
        {open && draft && pos ? createPortal(
          <div className="pd-scope">
            <div ref={sheetRef} className="pd-sheet" role="dialog" aria-label="Sort and filter" data-cols={pos.cols} style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.w, maxHeight: pos.maxH }} data-testid="pd-sortfilter-pop">
              <div className="pd-sbody">{children(draft, patch)}</div>
              {footer}
            </div>
          </div>, document.body) : null}
      </div>
    );
  }
  return (
    <div className="relative" ref={triggerRef}>
      {trigger}
      {open && draft ? (
        <div ref={sheetRef} className="pd-pop" role="dialog" aria-label="Sort and filter" style={{ width: `min(${width}px, calc(100vw - 32px))` }} data-testid="pd-sortfilter-pop">
          {children(draft, patch)}
          {footer}
        </div>
      ) : null}
    </div>
  );
}

// Removable active-filter chips; hidden when empty.
export function ChipRow({ chips, onClearAll }) {
  if (!chips.length) return null;
  return (
    <div className="pd-fchips" data-testid="pd-active-chips">
      <span className="pd-fchips-count" data-testid="pd-active-count">{chips.length} {chips.length === 1 ? "filter" : "filters"} active</span>
      {chips.map((c) => (
        <button key={c.k} type="button" className="pd-fchip" onClick={c.onRemove} aria-label={`Remove filter ${c.label}`} data-testid={`pd-fchip-${c.k}`}>{c.label}<X size={10} strokeWidth={2.4} /></button>
      ))}
      <button type="button" className="pd-btn txt" style={{ fontSize: 12.5, padding: "4px 6px", minHeight: 0 }} onClick={onClearAll} data-testid="pd-fchips-clear">Clear all</button>
    </div>
  );
}

// Shared upload lightbox: drop zone + Choose files, file list, routing line.
export function UploadLightbox({ title, sub, accept, onUpload, onClose }) {
  const [files, setFiles] = useState([]);
  const [over, setOver] = useState(false);
  const inp = useRef(null);
  useEffect(() => { const k = (e) => e.key === "Escape" && onClose(); document.addEventListener("keydown", k); return () => document.removeEventListener("keydown", k); }, [onClose]);
  const n = files.length;
  return createPortal(
    <div className="pd-scope pd-scrimx" onMouseDown={onClose} data-testid="pd-upload-lightbox">
      <div className="pd-lb" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(e) => e.stopPropagation()}>
        <input ref={inp} type="file" multiple accept={accept} className="hidden" onChange={(e) => { setFiles([...e.target.files]); e.target.value = ""; }} data-testid="pd-lb-input" />
        <div className="flex items-start gap-3 px-5 pt-[18px] pb-1.5">
          <div className="flex-1"><div className="text-[17px] font-bold" style={{ color: T.ink }}>{title}</div><div className="text-[13px] mt-[3px]" style={{ color: T.sub }}>{sub}</div></div>
          <button type="button" className="pd-btn ico" onClick={onClose} aria-label="Close" data-testid="pd-lb-close"><X size={15} /></button>
        </div>
        <div className="px-5 py-3 flex flex-col gap-3">
          {!n ? (
            <div className={`pd-drop${over ? " on" : ""}`} onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setOver(true); }} onDragLeave={() => setOver(false)}
              onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setOver(false); setFiles([...e.dataTransfer.files]); }} data-testid="pd-lb-drop">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.sub} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 7V5a2 2 0 0 1 2-2h2" /><path d="M17 3h2a2 2 0 0 1 2 2v2" /><path d="M21 17v2a2 2 0 0 1-2 2h-2" /><path d="M7 21H5a2 2 0 0 1-2-2v-2" /><path d="M7 12h10" /></svg>
              <div>Drop files here, or</div>
              <button type="button" className="pd-btn" onClick={() => inp.current?.click()} data-testid="pd-lb-choose">Choose files</button>
              <div className="text-[12px]" style={{ color: T.faint }}>PDF, JPG, PNG, HEIC · up to 50 MB each · combined scans are split into documents</div>
            </div>
          ) : (
            <div>
              <div className="rounded-[10px] overflow-hidden" style={{ border: `1px solid ${T.hair}` }} data-testid="pd-lb-files">
                {files.map((f) => <div key={f.name + f.size} className="flex items-center gap-2 px-3 py-2 text-[13px]" style={{ borderBottom: `1px solid ${T.soft}` }}><span className="flex-1 truncate font-semibold" style={{ color: T.ink }}>{f.name}</span><span style={{ color: T.faint }}>{Math.max(1, Math.round(f.size / 1024))} KB</span></div>)}
              </div>
              <button type="button" className="pd-btn txt" style={{ paddingLeft: 0 }} onClick={() => inp.current?.click()} data-testid="pd-lb-change">Change files</button>
            </div>
          )}
          <div className="text-[12px]" style={{ color: T.faint }} data-testid="pd-lb-limits">PDF, JPG, PNG, HEIC · up to 50 MB each · combined scans are split into documents</div>
          <div className="text-[12.5px] rounded-[10px] px-3 py-2.5" style={{ background: T.rail, color: T.sub }} data-testid="pd-lb-route">Lens sorts each file: patient documents go to Chart Documents, letters and notices to Clinic Documents.</div>
        </div>
        <div className="flex items-center gap-2 px-5 py-3" style={{ borderTop: `1px solid ${T.hair}` }}>
          <span className="flex-1" />
          <button type="button" className="pd-btn" onClick={onClose} data-testid="pd-lb-cancel">Cancel</button>
          <button type="button" className="pd-btn pri" disabled={!n} onClick={() => { onUpload(files); onClose(); }} data-testid="pd-lb-upload">{n ? `Upload ${n} ${n === 1 ? "file" : "files"}` : "Upload"}</button>
        </div>
      </div>
    </div>, document.body);
}
